// frontend-website/src/telemetry/sessionManager.js
// Session management for telemetry tracking

/**
 * SessionManager - Manages user sessions with automatic timeout
 */
export class SessionManager {
    constructor(sessionTimeout = 30 * 60 * 1000) { // 30 minutes default
        this.sessionTimeout = sessionTimeout;
        this.sessionId = null;
        this.sessionStartTime = null;
        this.lastActivityTime = null;
        this.storageKey = 'telemetry_session';
    }

    /**
     * Start or resume a session
     */
    startSession() {
        const stored = this._loadSession();

        // Check if stored session is still valid
        if (stored && this._isSessionValid(stored)) {
            // Resume existing session
            this.sessionId = stored.sessionId;
            this.sessionStartTime = stored.startTime;
            this.lastActivityTime = Date.now();
            this._saveSession();
            return false; // Not a new session
        } else {
            // Create new session
            this.sessionId = this._generateSessionId();
            this.sessionStartTime = Date.now();
            this.lastActivityTime = Date.now();
            this._saveSession();
            return true; // New session
        }
    }

    /**
     * Get current session ID
     */
    getSessionId() {
        // Update activity time
        this.lastActivityTime = Date.now();
        this._saveSession();
        return this.sessionId;
    }

    /**
     * Get session duration in milliseconds
     */
    getSessionDuration() {
        if (!this.sessionStartTime) {
            return 0;
        }
        return Date.now() - this.sessionStartTime;
    }

    /**
     * Check if this is a new session
     */
    isNewSession() {
        const duration = this.getSessionDuration();
        return duration < 5000; // Less than 5 seconds old = new session
    }

    /**
     * End the current session
     */
    endSession() {
        this._clearSession();
    }

    /**
     * Generate a unique session ID
     * @private
     */
    _generateSessionId() {
        // Generate a random UUID-like string
        return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Check if stored session is still valid
     * @private
     */
    _isSessionValid(stored) {
        if (!stored || !stored.lastActivity) {
            return false;
        }

        const timeSinceLastActivity = Date.now() - stored.lastActivity;
        return timeSinceLastActivity < this.sessionTimeout;
    }

    /**
     * Save session to storage
     * @private
     */
    _saveSession() {
        try {
            const sessionData = {
                sessionId: this.sessionId,
                startTime: this.sessionStartTime,
                lastActivity: this.lastActivityTime
            };
            localStorage.setItem(this.storageKey, JSON.stringify(sessionData));
        } catch (error) {
            console.warn('[SessionManager] Failed to save session:', error);
        }
    }

    /**
     * Load session from storage
     * @private
     */
    _loadSession() {
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.warn('[SessionManager] Failed to load session:', error);
        }
        return null;
    }

    /**
     * Clear session from storage
     * @private
     */
    _clearSession() {
        try {
            localStorage.removeItem(this.storageKey);
        } catch (error) {
            console.warn('[SessionManager] Failed to clear session:', error);
        }
        this.sessionId = null;
        this.sessionStartTime = null;
        this.lastActivityTime = null;
    }
}
